/**
 * Security Service
 *
 * Handles security settings and monitoring operations
 */

import { BaseService } from "../base/BaseService";
import { captureException } from "@/services/sentry";
import {
  SecuritySettings,
  LoginHistoryEntry,
  UpdateSecuritySettingsResponse,
  AuthMetadataResponse,
  UpdateEmailResponse,
  UpdatePhoneResponse,
  FailedAttemptsResponse,
  LockedAccountsResponse,
  AccountLockoutStatus,
  LogLoginAttemptParams,
  ActiveDevice,
  FlagSuspiciousActivityResponse,
  UnlockAccountResponse,
  VerificationResponse,
  VerificationStatusResponse,
  RefreshTokenResponse,
  RevokeTokenResponse,
  ActiveToken,
} from "./types";

const AUTH_SERVICE_URL = "auth";

const UNAVAILABLE_LOCKOUT_STATUS: AccountLockoutStatus = {
  locked: false,
  recentFailedAttempts: 0,
  lockoutWindowMinutes: 15,
  maxAttempts: 5,
  checkUnavailable: true,
};

/**
 * Report pre-login lockout-check failures — these fail-closed and block all sign-in.
 * Never include email / PII in the Sentry payload.
 */
function reportLockoutCheckUnavailable(reason: string, statusCode?: number): void {
  const error = new Error(`Pre-login lockout check unavailable: ${reason}`);
  error.name = "LockoutCheckUnavailableError";
  captureException(error, {
    level: "error",
    tags: {
      feature: "auth",
      availability: "login",
      endpoint: "security/check-lockout",
      reason,
      ...(typeof statusCode === "number" ? { status_code: String(statusCode) } : {}),
    },
    fingerprint: ["auth", "lockout-check-unavailable", reason],
  });
}

function resolveLockoutNetworkFailureReason(error: unknown): string {
  if (error instanceof TypeError) {
    return "network_error";
  }
  if (error instanceof Error) {
    return "request_failed";
  }
  return "unknown_error";
}

/**
 * Security Service
 * Handles security settings and monitoring
 */
export class SecurityService extends BaseService {
  constructor() {
    super(AUTH_SERVICE_URL);
  }

  /**
   * Get security settings
   * GET /profile/:userId/security
   */
  getSecuritySettings(userId: string): Promise<SecuritySettings> {
    return this.get(`/profile/${userId}/security`);
  }

  /**
   * Update security settings
   * PUT /profile/:userId/security
   */
  updateSecuritySettings(
    userId: string,
    settings: Partial<SecuritySettings>
  ): Promise<UpdateSecuritySettingsResponse> {
    return this.put(`/profile/${userId}/security`, settings);
  }

  /**
   * Get auth metadata
   * GET /profile/:userId/auth-metadata
   */
  getAuthMetadata(userId: string): Promise<AuthMetadataResponse> {
    return this.get(`/profile/${userId}/auth-metadata`);
  }

  /**
   * Update email
   * PUT /profile/:userId/email
   */
  updateEmail(userId: string, newEmail: string): Promise<UpdateEmailResponse> {
    return this.put(`/profile/${userId}/email`, { newEmail });
  }

  /**
   * Update phone
   * PUT /profile/:userId/phone
   */
  updatePhone(userId: string, newPhone: string): Promise<UpdatePhoneResponse> {
    return this.put(`/profile/${userId}/phone`, { newPhone });
  }

  /**
   * Pre-login account lockout check (public endpoint, no auth required).
   * POST /security/check-lockout
   */
  async checkAccountLockout(email: string): Promise<AccountLockoutStatus> {
    try {
      const response = await fetch(`${this.baseUrl}/security/check-lockout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      if (!response.ok) {
        reportLockoutCheckUnavailable(`http_${response.status}`, response.status);
        return { ...UNAVAILABLE_LOCKOUT_STATUS };
      }

      const payload = (await response.json()) as { data?: AccountLockoutStatus };
      const data = payload.data ?? {
        locked: false,
        recentFailedAttempts: 0,
        lockoutWindowMinutes: 15,
        maxAttempts: 5,
      };
      return data;
    } catch (error: unknown) {
      reportLockoutCheckUnavailable(resolveLockoutNetworkFailureReason(error));
      return { ...UNAVAILABLE_LOCKOUT_STATUS };
    }
  }

  /**
   * Log a login attempt (public endpoint; uses CSRF token when available).
   * POST /security/log-login
   */
  async logLoginAttempt(params: LogLoginAttemptParams): Promise<void> {
    try {
      await this.post("/security/log-login", {
        email: params.email.trim().toLowerCase(),
        userId: params.userId ?? null,
        success: params.success,
        failureReason: params.failureReason,
        ipAddress: "unknown",
        userAgent: globalThis.navigator !== undefined ? globalThis.navigator.userAgent : "unknown",
      });
    } catch (error) {
      console.warn("Failed to log login attempt (non-critical):", error);
    }
  }

  /**
   * Get login history
   * GET /security/login-history/:userId
   */
  async getLoginHistory(userId: string, limit: number = 50): Promise<LoginHistoryEntry[]> {
    const response = await this.get<{ success: boolean; data: LoginHistoryEntry[] }>(
      `/security/login-history/${userId}?limit=${limit}`
    );
    return response.data ?? [];
  }

  /**
   * Get failed login attempts
   * GET /security/failed-attempts/:userId
   */
  async getFailedAttempts(userId: string): Promise<FailedAttemptsResponse> {
    const response = await this.get<{
      success: boolean;
      data: {
        attempts?: Array<{
          attempt_at: string;
          ip_address?: string;
          failure_reason?: string;
        }>;
        recentCount?: number;
        accountLocked?: boolean;
      };
    }>(`/security/failed-attempts/${userId}`);

    const payload = response.data;
    const attempts = (payload?.attempts ?? []).map((entry) => ({
      timestamp: entry.attempt_at,
      ipAddress: entry.ip_address ?? "unknown",
      reason: entry.failure_reason,
    }));

    return {
      count: payload?.recentCount ?? 0,
      accountLocked: payload?.accountLocked ?? false,
      attempts,
    };
  }

  /**
   * List accounts currently in lockout (admin only)
   * GET /security/locked-accounts
   */
  async getLockedAccounts(): Promise<LockedAccountsResponse> {
    const response = await this.get<{
      success: boolean;
      data: LockedAccountsResponse["accounts"];
      meta?: { lockoutWindowMinutes: number; maxAttempts: number };
    }>("/security/locked-accounts");

    return {
      accounts: response.data ?? [],
      lockoutWindowMinutes: response.meta?.lockoutWindowMinutes ?? 15,
      maxAttempts: response.meta?.maxAttempts ?? 5,
    };
  }

  /**
   * Get active devices
   * GET /security/active-devices/:userId
   */
  async getActiveDevices(userId: string): Promise<ActiveDevice[]> {
    const response = await this.get<{ success: boolean; data: ActiveDevice[] }>(
      `/security/active-devices/${userId}`
    );
    return response.data ?? [];
  }

  /**
   * Flag suspicious activity
   * POST /security/flag-suspicious
   */
  flagSuspiciousActivity(
    userId: string,
    reason: string,
    metadata?: Record<string, unknown>
  ): Promise<FlagSuspiciousActivityResponse> {
    return this.post("/security/flag-suspicious", { userId, reason, metadata });
  }

  /**
   * Unlock account (admin only)
   * POST /security/unlock-account
   */
  unlockAccount(targetUserId: string, email?: string): Promise<UnlockAccountResponse> {
    return this.post("/security/unlock-account", {
      userId: targetUserId,
      email,
    });
  }

  /**
   * Send email verification
   * POST /verification/email/send
   */
  sendEmailVerification(userId: string, email: string): Promise<VerificationResponse> {
    return this.post("/verification/email/send", { userId, email });
  }

  /**
   * Confirm email verification
   * POST /verification/email/confirm
   */
  confirmEmailVerification(userId: string, token: string): Promise<VerificationResponse> {
    return this.post("/verification/email/confirm", { userId, token });
  }

  /**
   * Send phone verification
   * POST /verification/phone/send
   */
  sendPhoneVerification(userId: string, phone: string): Promise<VerificationResponse> {
    return this.post("/verification/phone/send", { userId, phone });
  }

  /**
   * Confirm phone verification
   * POST /verification/phone/confirm
   */
  confirmPhoneVerification(userId: string, token: string): Promise<VerificationResponse> {
    return this.post("/verification/phone/confirm", { userId, token });
  }

  /**
   * Get verification status
   * GET /verification/status/:userId
   */
  getVerificationStatus(userId: string): Promise<VerificationStatusResponse> {
    return this.get(`/verification/status/${userId}`);
  }

  /**
   * Refresh access token
   * POST /tokens/refresh
   */
  async refreshToken(): Promise<RefreshTokenResponse> {
    const { supabase } = await import("@/integrations/supabase/client");
    const { data, error } = await supabase.auth.refreshSession();
    return { data, error };
  }

  /**
   * Revoke token (sign out)
   * POST /tokens/revoke
   */
  revokeToken(): Promise<RevokeTokenResponse> {
    // This is handled by authService.signout()
    return Promise.resolve({ success: true });
  }

  /**
   * Get active tokens for user
   * GET /tokens/active/:userId
   */
  getActiveTokens(userId: string): Promise<ActiveToken[]> {
    return this.get(`/tokens/active/${userId}`);
  }
}
